/**
 * PostHog, loaded off the critical path (browser only).
 *
 * posthog-js is reachable ONLY through the dynamic import in loadPosthog(): it never enters a page's
 * initial bundle. PostHogLoader imports this module after window `load`, inside requestIdleCallback
 * (see PostHogLoader for its timing), and calls startPosthog(). Everything here is fire-and-forget: no function throws, returns a promise or
 * waits on PostHog, so a capture can never block or break the setup / payment flow. Captures made
 * before the library is ready are queued (capped) and flushed once it is.
 *
 * Identity: the first-party `_lmid` cookie is the anonymous id. It is passed to PostHog as
 * bootstrap.distinctID, so the id sent with setup-session calls before PostHog loads and PostHog's
 * own distinct id agree.
 *
 * Skipped entirely on the demo host and in the admin area; local dev only with ?qa=1 in the URL.
 */
import type { PostHog } from "posthog-js"

// Public client key (it ships in every page that loads PostHog).
const PH_TOKEN = "phc_w8jiUgnse2pJQ9WvcSQ3QEvZEhamGVzoAoXDdTnBYMoB"

const LMID_COOKIE = "_lmid"
const LANDING_KEY = "_lm_land"
const RECORDING_KEY = "_lm_rec"
const DEV_QA_KEY = "_lm_qa"
const ONCE_PREFIX = "_lm1:"
const MAX_QUEUE = 50
const ID_RE = /^[A-Za-z0-9_\-:.@$]{1,200}$/

/** Landing-URL params kept as session super properties (every utm_* is kept as well). */
const LANDING_PARAMS = new Set([
    "gclid", "gbraid", "wbraid", "dclid", "msclkid", "fbclid", "ttclid", "rdt_cid",
    "campaignid", "adgroupid", "keyword", "matchtype", "creative", "ad", "adid", "ad_id", "network", "device", "targetid",
    "msq", "loc_physical_ms", "utm_network", "utm_loc_physical_ms", "ref", "qa",
])

/**
 * Never stored or sent: sign-in / magic-link / OAuth secrets that ride on a landing URL (Supabase token_hash and code,
 * access/refresh/id tokens, signatures, one-time codes). The fragment is dropped too (implicit-flow tokens live there).
 */
const SECRET_PARAM_RE = /token|secret|passw|jwt|api_?key|signature|^(code|sig|otp|nonce|state|auth|session)$/i

/** The landing URL without its fragment and secret params (null when it does not parse). */
export function cleanLandingUrl(href: string): URL | null {
    try {
        const url = new URL(href)
        url.hash = ""
        for (const key of [...new Set(url.searchParams.keys())]) if (SECRET_PARAM_RE.test(key)) url.searchParams.delete(key)
        return url
    } catch {
        return null
    }
}

/**
 * Exception events carry the error text verbatim (an Error("password=X cvc=123 card 4242 4242 4242 4242") would reach
 * PostHog as is). For `$exception` events only, every string in the exception properties ($exception_list values,
 * types and frames, $exception_message, $exception_values, $sentry_exception*) is scrubbed: the value after a
 * pass / password / pwd / cvc / cvv / csc / security code key (any case, also camelCase: newPassword, cardCvc) followed
 * by = or :, and any card number: whole digit groups (single spaces or dashes between groups, never part of a group),
 * 13-19 digits, first digit 2-6, Luhn-valid. Strings are cut at 4K chars before scanning. Every other event is untouched.
 */
const ci = (word: string) => word.replace(/[a-z]/g, (c) => `[${c}${c.toUpperCase()}]`)
const SECRET_KEY = `(?:${ci("pass")}(?:${ci("word")}|${ci("wd")})?|${ci("pwd")}|${ci("cvc")}2?|${ci("cvv")}2?|${ci("csc")}|${ci("security")}[-_ ]?${ci("code")})`
// Key prefix: at most 6 parts, each a separated word (new_, card-) or a camelCase word (new, user, New); every part is
// bounded, so the scan stays linear on hostile input ("1-1-1-..."). No `i` flag: the camelCase split needs case.
const SECRET_KV_RE = new RegExp(`(\\b(?:[A-Za-z0-9]{1,32}[_-]|[A-Z]?[a-z0-9]{1,32}(?=[A-Z])){0,6}${SECRET_KEY}\\b["'\\]]?\\s*[=:]\\s*)("(?:\\\\.|[^"\\\\])*"|'(?:\\\\.|[^'\\\\])*'|[^\\s"'&,;)}\\]]+)`, "g")
/** Whole digit groups with single spaces or dashes between them. */
const DIGIT_RUN_RE = /\b\d+(?:[ -]\d+)*\b/g
const EXCEPTION_PROP_RE = /^\$(?:sentry_)?exception/
const MAX_SCRUB_DEPTH = 10
const MAX_SCRUB_CHARS = 4096

function luhn(digits: string): boolean {
    let sum = 0
    for (let i = 0; i < digits.length; i++) {
        let d = digits.charCodeAt(digits.length - 1 - i) - 48
        if (i % 2 === 1) {
            d *= 2
            if (d > 9) d -= 9
        }
        sum += d
    }
    return sum % 10 === 0
}

const isCardNumber = (digits: string) => digits.length >= 13 && digits.length <= 19 && digits[0] >= "2" && digits[0] <= "6" && luhn(digits)

/**
 * One run of whole digit groups: every stretch of consecutive whole groups (never part of a group) that is a card
 * number becomes [card]. A 13-digit ms timestamp or a 17-digit id starting with 1 stays as it is.
 */
function redactCards(run: string): string {
    const groups: Array<{ start: number; end: number; digits: string }> = []
    const re = /\d+/g
    for (let m = re.exec(run); m; m = re.exec(run)) groups.push({ start: m.index, end: m.index + m[0].length, digits: m[0] })
    let out = ""
    let last = 0
    for (let a = 0; a < groups.length; ) {
        let hitEnd = -1
        let digits = ""
        for (let b = a; b < groups.length; b++) {
            digits += groups[b].digits
            if (digits.length > 19) break
            if (isCardNumber(digits)) hitEnd = b
        }
        if (hitEnd < 0) {
            a++
            continue
        }
        out += run.slice(last, groups[a].start) + "[card]"
        last = groups[hitEnd].end
        a = hitEnd + 1
    }
    return last ? out + run.slice(last) : run
}

export function scrubSecrets(text: string): string {
    const capped = text.length > MAX_SCRUB_CHARS ? text.slice(0, MAX_SCRUB_CHARS) : text
    return capped.replace(SECRET_KV_RE, "$1[redacted]").replace(DIGIT_RUN_RE, redactCards)
}

/**
 * Session replay: incremental input events (rrweb type 3, source 5) on node id -1 are dropped from every $snapshot.
 * id -1 is a node the recorder never mirrored: a detached copy of a form field. The Meta pixel copies the form's inputs
 * into detached nodes on each button click; the recorder's value-setter hook logs those copies with id -1, outside
 * blockSelector and password masking, so the typed password would travel in plain text. A replay cannot apply an event
 * on id -1 anyway, so nothing visible is lost. Mutations that add a node under an unmirrored parent (-1) go the same way.
 */
const RR_INCREMENTAL = 3
const RR_SOURCE_MUTATION = 0
const RR_SOURCE_INPUT = 5
const mirrored = (id: unknown) => typeof id === "number" && id > 0

type RrEvent = { type?: number; data?: { source?: number; id?: unknown; adds?: unknown; texts?: unknown; attributes?: unknown } }

function scrubReplayItem(item: unknown): unknown {
    const ev = item as RrEvent | null
    if (!ev || ev.type !== RR_INCREMENTAL || !ev.data) return item
    if (ev.data.source === RR_SOURCE_INPUT) return mirrored(ev.data.id) ? item : null
    // Uncompressed mutation (compressed ones carry gzipped strings, and the recorder only serialises mirrored nodes).
    if (ev.data.source !== RR_SOURCE_MUTATION || !Array.isArray(ev.data.adds)) return item
    const keep = (a: { parentId?: unknown; id?: unknown }) => mirrored(a?.parentId ?? a?.id)
    const adds = (ev.data.adds as Array<{ parentId?: unknown }>).filter(keep)
    const texts = Array.isArray(ev.data.texts) ? (ev.data.texts as Array<{ id?: unknown }>).filter((t) => mirrored(t?.id)) : ev.data.texts
    const attributes = Array.isArray(ev.data.attributes) ? (ev.data.attributes as Array<{ id?: unknown }>).filter((t) => mirrored(t?.id)) : ev.data.attributes
    if (adds.length === (ev.data.adds as unknown[]).length && texts === ev.data.texts && attributes === ev.data.attributes) return item
    return { ...ev, data: { ...ev.data, adds, texts, attributes } }
}

export function scrubSnapshotEvent<T extends { event?: string; properties?: Record<string, unknown> } | null>(e: T): T | null {
    if (!e || e.event !== "$snapshot" || !e.properties) return e
    const raw = e.properties.$snapshot_data
    const list = Array.isArray(raw) ? raw : [raw]
    let changed = false
    const kept: unknown[] = []
    for (const item of list) {
        const out = scrubReplayItem(item)
        if (out !== item) changed = true
        if (out) kept.push(out)
    }
    if (!changed) return e
    if (!kept.length) return null
    return { ...e, properties: { ...e.properties, $snapshot_data: Array.isArray(raw) ? kept : kept[0] } }
}

function scrubDeep(value: unknown, depth: number): unknown {
    if (typeof value === "string") return scrubSecrets(value)
    if (!value || typeof value !== "object" || depth > MAX_SCRUB_DEPTH) return value
    if (Array.isArray(value)) return value.map((v) => scrubDeep(v, depth + 1))
    const out: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) out[k] = scrubDeep(v, depth + 1)
    return out
}

export function scrubExceptionEvent<T extends { event?: string; properties?: Record<string, unknown> } | null>(e: T): T {
    if (!e || e.event !== "$exception" || !e.properties) return e
    const properties: Record<string, unknown> = { ...e.properties }
    for (const key of Object.keys(properties)) if (EXCEPTION_PROP_RE.test(key)) properties[key] = scrubDeep(properties[key], 0)
    return { ...e, properties }
}

/** Never recorded: password fields, card fields (Evervault iframes / containers) and anything tagged ph-no-capture. */
const BLOCK_SELECTOR = [
    ".ph-no-capture",
    "input[type=\"password\"]",
    "input[autocomplete=\"current-password\"]",
    "input[autocomplete=\"new-password\"]",
    "input[autocomplete^=\"cc-\"]",
    "iframe[src*=\"evervault\"]",
    "[data-evervault]",
].join(", ")

/**
 * Replay input masking (maskAllInputs + this maskInputFn): every input value is masked in replays except an e-mail or
 * a handle / search field. Password-like, card-like and blocked fields are always masked, also after a show-password
 * toggle (rrweb keeps data-rr-is-password) and on detached copies of a field (cloneNode keeps the attributes).
 */
const REPLAY_SECRET_RE = /pass|pwd|cvc|cvv|csc|card|cc-|security|secret|token|otp|code|pin/i
const REPLAY_SHOWN_RE = /e-?mail|user(?:name)?|handle|search|query/i

export function replayInputValue(text: string, el?: Element | null): string {
    const masked = "*".repeat(text.length)
    try {
        if (!el || typeof el.getAttribute !== "function") return masked
        if (el.hasAttribute("data-rr-is-password") || el.matches(BLOCK_SELECTOR)) return masked
        const attr = (name: string) => el.getAttribute(name) || ""
        const type = attr("type").toLowerCase()
        const ident = `${attr("name")} ${attr("id")} ${attr("autocomplete")}`
        if (type === "password" || REPLAY_SECRET_RE.test(ident) || scrubSecrets(text) !== text) return masked
        if (type === "email" || type === "search" || attr("role") === "combobox" || REPLAY_SHOWN_RE.test(ident)) return text
    } catch {
        /* masked */
    }
    return masked
}

const RECORDING_EVENTS = new Set(["checkout_started", "pay_pressed"])

/** Click ids only a paid-ad click puts on the landing URL. A session that landed with one is recorded (startPaidRecording). */
export const PAID_CLICK_IDS = ["gclid", "gbraid", "wbraid", "msclkid", "rdt_cid"] as const

/** The visitor's first real interaction: the touch beacon and a paid session's recorder wait for it (then an idle slot). */
const INTERACTION_EVENTS = ["pointerdown", "keydown", "touchstart", "scroll"] as const
const LISTEN_OPTS: AddEventListenerOptions = { capture: true, passive: true }

const TOUCH_URL = "/api/t/touch"
/** Once-flag of a landing without ad/utm params; a landing with them gets its own flag (touchOnceKey). */
const TOUCH_ONCE = "touch"
/** requestIdleCallback fallback timeout, and the cap of the idle wait where rIC is missing. */
const IDLE_TIMEOUT_MS = 10_000
const TOUCH_MAX_PARAMS = 60

let ph: PostHog | null = null
let state: "idle" | "waiting" | "loading" | "ready" | "off" = "idle"
const queue: Array<(p: PostHog) => void> = []
const onceMemory = new Set<string>()
let lastIdentify = ""
let interacted = false
let interactionArmed = false
const afterInteraction: Array<() => void> = []

function readCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
    return m ? decodeURIComponent(m[1]) : null
}

function writeCookie(name: string, value: string) {
    const secure = location.protocol === "https:" ? "; Secure" : ""
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax${secure}`
}

function newId(): string {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
    } catch {
        /* fall through */
    }
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`
}

function excludedContext(): boolean {
    return location.hostname.split(".")[0] === "demo" || location.pathname.startsWith("/app/app_admin")
}

function devAllowed(): boolean {
    if (process.env.NODE_ENV === "production") return true
    try {
        if (new URLSearchParams(location.search).get("qa") === "1") sessionStorage.setItem(DEV_QA_KEY, "1")
        return sessionStorage.getItem(DEV_QA_KEY) === "1"
    } catch {
        return false
    }
}

/** First-party anonymous id (created on first use, kept a year). */
export function getLmid(): string | null {
    if (typeof document === "undefined") return null
    try {
        let id = readCookie(LMID_COOKIE)
        if (!id || !ID_RE.test(id)) {
            id = newId()
            writeCookie(LMID_COOKIE, id)
        }
        return id
    } catch {
        return null
    }
}

/** The id every setup-session call carries: PostHog's distinct id once loaded, else `_lmid`. */
export function getAnalyticsId(): string | null {
    try {
        const id = ph?.get_distinct_id()
        if (id && ID_RE.test(id)) return id
    } catch {
        /* fall through */
    }
    return getLmid()
}

/** Landing record of one URL: cleaned landing_url plus its ad/utm params (first value per lower-cased key). */
function landingRecord(href: string): Record<string, string> | null {
    const url = cleanLandingUrl(href)
    if (!url) return null
    const props: Record<string, string> = { landing_url: url.href.slice(0, 1000) }
    url.searchParams.forEach((value, rawKey) => {
        const key = rawKey.toLowerCase()
        if (value && !(key in props) && (key.startsWith("utm_") || LANDING_PARAMS.has(key))) props[key] = value.slice(0, 300)
    })
    return props
}

/** Canonical ad/utm attribution of a landing record ("" for an organic, param-less landing). */
function attributionOf(props: Record<string, unknown>): string {
    return Object.keys(props)
        .filter((k) => k !== "landing_url" && k !== "qa")
        .sort()
        .map((k) => `${k}=${String(props[k])}`)
        .join("&")
}

/** Short stable hash (djb2, base 36) for once-flag keys. */
function shortHash(s: string): string {
    let h = 5381
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0
    return (h >>> 0).toString(36)
}

/** Touch once-flag key: one per distinct ad/utm attribution in the tab, one shared key for param-less landings. */
export function touchOnceKey(landingHref: string): string {
    const record = landingRecord(landingHref)
    const attribution = record ? attributionOf(record) : ""
    return attribution ? `${TOUCH_ONCE}:${shortHash(attribution)}` : TOUCH_ONCE
}

/**
 * Stores the landing URL and its ad/utm params for this browser-tab session. First landing wins, except that a page
 * load with an explicit `href` carrying a different ad/utm attribution (an ad click after an organic visit in the same
 * tab) replaces it. The implicit call (no href: getLandingUrl) only fills a missing record.
 */
export function rememberLanding(href?: string): void {
    if (typeof window === "undefined") return
    try {
        const existing = sessionStorage.getItem(LANDING_KEY)
        if (existing && !href) return
        const props = landingRecord(href || location.href)
        if (!props) return
        if (existing) {
            const attribution = attributionOf(props)
            let previous = ""
            try {
                previous = attributionOf(JSON.parse(existing) || {})
            } catch {
                /* unreadable record: replace it */
            }
            if (!attribution || attribution === previous) return
        }
        sessionStorage.setItem(LANDING_KEY, JSON.stringify(props))
    } catch {
        /* storage unavailable: nothing to remember */
    }
}

/** Landing URL of this session (null when unknown). */
export function getLandingUrl(): string | null {
    if (typeof window === "undefined") return null
    try {
        rememberLanding()
        const raw = sessionStorage.getItem(LANDING_KEY)
        return raw ? (JSON.parse(raw).landing_url as string) || null : null
    } catch {
        return null
    }
}

/** True the first time `key` is seen in this tab session (sessionStorage, memory fallback). */
export function phOnce(key: string): boolean {
    const full = ONCE_PREFIX + key
    try {
        if (sessionStorage.getItem(full)) return false
        sessionStorage.setItem(full, "1")
        return true
    } catch {
        if (onceMemory.has(full)) return false
        onceMemory.add(full)
        return true
    }
}

function onceDone(key: string): boolean {
    const full = ONCE_PREFIX + key
    try {
        return sessionStorage.getItem(full) === "1" || onceMemory.has(full)
    } catch {
        return onceMemory.has(full)
    }
}

/**
 * Runs `fn` in a true idle slot: requestIdleCallback with a long (10 s) fallback timeout. Where rIC is missing
 * (Safari), a 50 ms probe timer stands in: `fn` runs once a probe fires on time (the main thread was free), at the
 * latest after 10 s.
 */
function whenIdle(fn: () => void) {
    const safe = () => {
        try {
            fn()
        } catch {
            /* never throw */
        }
    }
    if (typeof window.requestIdleCallback === "function") {
        window.requestIdleCallback(safe, { timeout: IDLE_TIMEOUT_MS })
        return
    }
    const PROBE_MS = 50
    const deadline = Date.now() + IDLE_TIMEOUT_MS
    let due = Date.now() + PROBE_MS
    const probe = () => {
        const now = Date.now()
        if (now - due < 10 || now >= deadline) return safe()
        due = now + PROBE_MS
        setTimeout(probe, PROBE_MS)
    }
    setTimeout(probe, PROBE_MS)
}

/** One passive capture listener per interaction type, removed on the first event; queued callbacks run in an idle slot. */
function armInteraction() {
    if (interactionArmed) return
    interactionArmed = true
    const fire = () => {
        if (interacted) return
        interacted = true
        for (const type of INTERACTION_EVENTS) window.removeEventListener(type, fire, LISTEN_OPTS)
        const pending = afterInteraction.splice(0)
        if (pending.length)
            whenIdle(() =>
                pending.forEach((fn) => {
                    try {
                        fn()
                    } catch {
                        /* one callback never stops the others */
                    }
                })
            )
    }
    for (const type of INTERACTION_EVENTS) window.addEventListener(type, fire, LISTEN_OPTS)
}

/** Runs `fn` in an idle slot after the visitor's first interaction (at once, in an idle slot, if it already happened). */
function afterFirstInteraction(fn: () => void) {
    if (interacted) {
        whenIdle(fn)
        return
    }
    afterInteraction.push(fn)
    armInteraction()
}

/** Landing params remembered for this tab session (empty when unknown). */
function landingProps(): Record<string, string> {
    try {
        const raw = sessionStorage.getItem(LANDING_KEY)
        const parsed = raw ? JSON.parse(raw) : null
        return parsed && typeof parsed === "object" ? (parsed as Record<string, string>) : {}
    } catch {
        return {}
    }
}

/** True when this tab session landed from a paid-ad click (a click id only ad clicks carry). */
export function isPaidLanding(props: Record<string, unknown>): boolean {
    return PAID_CLICK_IDS.some((key) => typeof props[key] === "string" && (props[key] as string).trim() !== "")
}

/** Beacon body for /api/t/touch: landing URL, referrer and every query param (lower-cased keys, 300 chars each). */
export function buildTouchBody(input: {
    lmid: string | null
    landingHref: string
    referrer: string
    webdriver: boolean
    tz: string | null
    lang: string | null
    screen: string | null
}): string | null {
    if (!input.lmid || !ID_RE.test(input.lmid)) return null
    // Fragment and secret params (magic-link / OAuth tokens) never reach visitor_touches.
    const url = cleanLandingUrl(input.landingHref)
    if (!url) return null
    const params: Record<string, string> = {}
    let count = 0
    url.searchParams.forEach((value, rawKey) => {
        const key = rawKey.toLowerCase().slice(0, 64)
        if (!key || !value || key in params || count >= TOUCH_MAX_PARAMS) return
        params[key] = value.slice(0, 300)
        count++
    })
    return JSON.stringify({
        lmid: input.lmid,
        landing_url: url.href.slice(0, 2000),
        referrer: (input.referrer || "").slice(0, 1000),
        params,
        webdriver: input.webdriver,
        tz: input.tz ? input.tz.slice(0, 64) : null,
        lang: input.lang ? input.lang.slice(0, 35) : null,
        screen: input.screen,
    })
}

/** Sends this landing's one touch beacon (never awaited, never throws). */
function sendTouch(landingHref: string, onceKey: string) {
    try {
        if (!phOnce(onceKey)) return
        // A param-less page load later in this tab sends nothing more.
        if (onceKey !== TOUCH_ONCE) phOnce(TOUCH_ONCE)
        let tz: string | null = null
        try {
            tz = Intl.DateTimeFormat().resolvedOptions().timeZone || null
        } catch {
            /* ignore */
        }
        const body = buildTouchBody({
            lmid: getLmid(),
            landingHref,
            referrer: document.referrer,
            webdriver: navigator.webdriver === true,
            tz,
            lang: navigator.language || null,
            screen: typeof screen !== "undefined" ? `${screen.width}x${screen.height}` : null,
        })
        if (!body) return
        if (typeof navigator.sendBeacon === "function" && navigator.sendBeacon(TOUCH_URL, body)) return
        fetch(TOUCH_URL, { method: "POST", body, keepalive: true, credentials: "same-origin" }).catch(() => {})
    } catch {
        /* never throw */
    }
}

/**
 * Arms the touch beacon: it goes once per landing attribution in the tab session (touchOnceKey: a param-less landing
 * only when no touch went yet; an ad click after an organic visit sends its own), in an idle slot after the first
 * interaction, or at once when the page is hidden first (a visitor who leaves without touching the page still counts).
 */
function armTouch(landingHref: string) {
    const onceKey = touchOnceKey(landingHref)
    if (onceDone(onceKey)) return
    let sent = false
    const send = () => {
        if (sent) return
        sent = true
        document.removeEventListener("visibilitychange", onHidden)
        window.removeEventListener("pagehide", send)
        sendTouch(landingHref, onceKey)
    }
    const onHidden = () => {
        if (document.visibilityState === "hidden") send()
    }
    document.addEventListener("visibilitychange", onHidden)
    window.addEventListener("pagehide", send)
    afterFirstInteraction(send)
}

function run(fn: (p: PostHog) => void) {
    if (state === "off" || typeof window === "undefined") return
    if (ph) {
        try {
            fn(ph)
        } catch {
            /* never throw into the caller */
        }
        return
    }
    if (queue.length < MAX_QUEUE) queue.push(fn)
}

function startRecording(p: PostHog) {
    try {
        sessionStorage.setItem(RECORDING_KEY, "1")
    } catch {
        /* ignore */
    }
    if (!p.sessionRecordingStarted()) p.startSessionRecording({ event_trigger: true })
}

/**
 * Paid-ad session: start replay after the first interaction (idle slot), whatever the project's trigger settings
 * (event or URL triggers, match any or all). Sampling is overridden only on the first start: on a recorder that is
 * already running that override would force an extra full snapshot. No `_lm_rec` flag: each full page load of the
 * session starts again after its own first interaction, so the recorder never loads before one. Calls are a no-op when
 * replay is off in the project settings; card and password fields stay blocked (BLOCK_SELECTOR).
 */
function startPaidRecording(p: PostHog, tries = 0) {
    // posthog.init builds its replay module in a time-sliced task queue; an override sent before it exists is lost.
    if (!p.sessionRecording) {
        if (tries < 20) setTimeout(() => run((ready) => startPaidRecording(ready, tries + 1)), 250)
        return
    }
    const triggers = { url_trigger: true, event_trigger: true } as const
    p.startSessionRecording(p.sessionRecordingStarted() ? triggers : { ...triggers, sampling: true })
}

export function phCapture(event: string, props?: Record<string, unknown>): void {
    run((p) => {
        if (RECORDING_EVENTS.has(event)) startRecording(p)
        p.capture(event, props)
    })
}

/**
 * field_typed, once per field per session. Handle: the typed username; email: the full typed address and its domain
 * (owner rule: e-mails and handles may be captured). Name (the card form's cardholder name) and others: no value.
 */
export function phFieldTyped(field: "handle" | "email" | "name" | "other", raw: string | null | undefined): void {
    const value = (raw ?? "").trim()
    if (!value || !phOnce(`field:${field}`)) return
    const props: Record<string, unknown> = { field }
    if (field === "handle") props.value = value.replace(/^@+/, "").slice(0, 100)
    if (field === "email") {
        props.value = value.slice(0, 200)
        const at = value.lastIndexOf("@")
        if (at > 0 && at < value.length - 1) props.domain = value.slice(at + 1).toLowerCase().slice(0, 100)
    }
    phCapture("field_typed", props)
}

/**
 * Sections and attention (sections.ts): one page_sections summary per page view. Its module is imported in an idle
 * slot after PostHog is ready, so it never joins a page bundle (posthog-client itself is in the setup page's).
 */
function startSectionTracking(p: PostHog) {
    whenIdle(() => {
        import("./sections")
            .then((m) =>
                m.startSections({
                    capture: (event, props, beacon) =>
                        run((ready) => ready.capture(event, props, beacon ? { transport: "sendBeacon", send_instantly: true } : undefined)),
                    once: phOnce,
                    onPageview: (cb) => {
                        p.on("eventCaptured", (e: { event?: string } | undefined) => {
                            if (e?.event === "$pageview") cb()
                        })
                    },
                    idle: whenIdle,
                })
            )
            .catch(() => {})
    })
}

export function phIdentify(userId: string | null | undefined, traits: { email?: string | null; name?: string | null; handle?: string | null }): void {
    if (!userId) return
    const clean: Record<string, string> = {}
    for (const [k, v] of Object.entries(traits)) if (typeof v === "string" && v.trim()) clean[k] = v.trim()
    const key = `${userId}|${JSON.stringify(clean)}`
    if (key === lastIdentify) return
    lastIdentify = key
    run((p) => p.identify(userId, clean))
}

function loadPosthog(landingHref: string) {
    if (state !== "waiting") return
    state = "loading"
    const lmid = getLmid()
    import("posthog-js")
        .then(({ default: posthog }) => {
            let resumeRecording = false
            try {
                resumeRecording = sessionStorage.getItem(RECORDING_KEY) === "1"
            } catch {
                /* ignore */
            }
            posthog.init(PH_TOKEN, {
                api_host: "/_ev",
                ui_host: "https://us.posthog.com",
                persistence: "localStorage",
                person_profiles: "identified_only",
                capture_pageview: "history_change",
                // Behaviour capture (paid plan, owner budget: analytics $120 a month). Each piece is a lazy PostHog
                // extension that loads after init, which itself runs after window load + idle. Clicks and submits only:
                // `change` (every field blur) is dropped as the noisiest target with no value captured anyway.
                // Card fields (cross-origin Evervault iframes inside .ph-no-capture), password inputs and
                // cc-/cvc-/pass-named fields are never captured by autocapture, rage/dead clicks or heatmaps.
                capture_pageleave: true,
                autocapture: { dom_event_allowlist: ["click", "submit"], capture_copied_text: false },
                rageclick: true,
                capture_heatmaps: true,
                capture_dead_clicks: true,
                capture_exceptions: { capture_unhandled_errors: true, capture_unhandled_rejections: true, capture_console_errors: false },
                capture_performance: { web_vitals: true },
                disable_surveys: true,
                disable_product_tours: true,
                disable_web_experiments: true,
                disable_conversations: true,
                advanced_disable_feature_flags_on_first_load: true,
                // Replay starts on checkout_started / pay_pressed (startRecording; resumes after a reload in that tab) and, in a
                // paid-ad session, after the first interaction (startPaidRecording). Never loaded at init otherwise.
                disable_session_recording: !resumeRecording,
                bootstrap: lmid ? { distinctID: lmid } : undefined,
                before_send: (event) => (excludedContext() ? null : scrubSnapshotEvent(scrubExceptionEvent(event))),
                session_recording: {
                    // Every input goes through replayInputValue: only e-mail and handle fields are shown.
                    maskAllInputs: true,
                    maskInputOptions: { password: true },
                    maskInputFn: replayInputValue,
                    blockSelector: BLOCK_SELECTOR,
                },
            })
            ph = posthog
            state = "ready"
            try {
                // Keep `_lmid` equal to PostHog's anonymous id if PostHog already had one from an earlier visit.
                const id = posthog.get_distinct_id()
                if (posthog.get_property("$user_state") !== "identified" && id && ID_RE.test(id) && id !== lmid) writeCookie(LMID_COOKIE, id)
                const raw = sessionStorage.getItem(LANDING_KEY)
                if (raw) posthog.register_for_session(JSON.parse(raw))
            } catch {
                /* ignore */
            }
            const pending = queue.splice(0)
            for (const fn of pending) run(fn)
            try {
                // This page load's own URL first (an ad click after an organic landing in the same tab), else the session's landing.
                if (isPaidLanding(landingRecord(landingHref) || {}) || isPaidLanding(landingProps()))
                    afterFirstInteraction(() => run((p) => startPaidRecording(p)))
            } catch {
                /* ignore */
            }
            try {
                startSectionTracking(posthog)
            } catch {
                /* ignore */
            }
        })
        .catch(() => {
            state = "off"
            queue.length = 0
        })
}

/**
 * Called by PostHogLoader after window load + idle time (the loader imports this module only then,
 * so neither this module nor posthog-js is in any page's initial bundle). `landingHref` is the URL
 * the page opened with, read by the loader at mount.
 */
export function startPosthog(landingHref?: string): void {
    if (typeof window === "undefined" || state !== "idle") return
    try {
        if (landingHref && new URL(landingHref).searchParams.get("qa") === "1") sessionStorage.setItem(DEV_QA_KEY, "1")
    } catch {
        /* ignore */
    }
    try {
        if (excludedContext() || !devAllowed()) {
            state = "off"
            queue.length = 0
            return
        }
        state = "waiting"
        const href = landingHref || location.href
        rememberLanding(href)
        armInteraction()
        armTouch(href)
        loadPosthog(href)
    } catch {
        state = "off"
    }
}
